(function (global) {
  'use strict';

  const SCHEMA_VERSION = '1.0.0';
  const ALLOWED_QUESTION_TYPES = ['single-choice', 'multi-choice', 'number', 'text', 'boolean'];
  const ALLOWED_VISIBILITY_OPERATORS = ['exists', 'equals', 'in', 'isTrue'];
  const ID_PATTERN = /^[A-Za-z][A-Za-z0-9._-]*$/;

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function isPrimitive(value) {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  }

  function cloneValue(value) {
    if (Array.isArray(value)) {
      return value.map(cloneValue);
    }

    if (isObject(value)) {
      const copy = {};
      Object.keys(value).forEach(function (key) {
        copy[key] = cloneValue(value[key]);
      });
      return copy;
    }

    return value;
  }

  function getQuestionnaireEngine(config) {
    if (!isObject(config)) {
      return null;
    }

    if (isObject(config.questionnaireEngine)) {
      return config.questionnaireEngine;
    }

    if (config.schemaVersion && (config.questionRegistry || config.questionnaires || config.sections)) {
      return config;
    }

    return null;
  }

  function normalizeRawAnswer(rawAnswer) {
    if (isObject(rawAnswer) && Object.prototype.hasOwnProperty.call(rawAnswer, 'value')) {
      return {
        value: cloneValue(rawAnswer.value),
        unit: Object.prototype.hasOwnProperty.call(rawAnswer, 'unit') ? cloneValue(rawAnswer.unit) : null
      };
    }

    return {
      value: cloneValue(rawAnswer),
      unit: null
    };
  }

  function getAnswerValue(answers, answerKey) {
    if (!isObject(answers) || !Object.prototype.hasOwnProperty.call(answers, answerKey)) {
      return { exists: false, value: undefined };
    }

    const normalized = normalizeRawAnswer(answers[answerKey]);
    return {
      exists: normalized.value !== undefined && normalized.value !== null,
      value: normalized.value
    };
  }

  function isMissingRequiredValue(value) {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  }

  function valuesEqual(left, right) {
    return left === right;
  }

  function containsValue(values, candidate) {
    return values.some(function (value) {
      return valuesEqual(value, candidate);
    });
  }

  function validateIdentifier(value, path, errors) {
    if (!isNonEmptyString(value) || !ID_PATTERN.test(value)) {
      errors.push(path + ' must be a stable identifier containing only letters, numbers, periods, underscores, or hyphens.');
    }
  }

  function validateOptionalKey(value, path, errors) {
    if (value !== null && !isNonEmptyString(value)) {
      errors.push(path + ' must be a non-empty string or null.');
    }
  }

  function validateVisibilityRules(rules, path, knownAnswerKeys, errors) {
    if (!Array.isArray(rules)) {
      errors.push(path + ' must be an array.');
      return;
    }

    rules.forEach(function (rule, index) {
      const rulePath = path + '[' + index + ']';

      if (!isObject(rule)) {
        errors.push(rulePath + ' must be an object.');
        return;
      }

      if (!isNonEmptyString(rule.answerKey)) {
        errors.push(rulePath + '.answerKey is required.');
      } else if (knownAnswerKeys && !knownAnswerKeys.has(rule.answerKey)) {
        errors.push(rulePath + '.answerKey must reference a registered answerKey.');
      }

      if (!ALLOWED_VISIBILITY_OPERATORS.includes(rule.operator)) {
        errors.push(rulePath + '.operator must be one of: ' + ALLOWED_VISIBILITY_OPERATORS.join(', ') + '.');
      }

      if (rule.operator === 'equals' && !Object.prototype.hasOwnProperty.call(rule, 'value')) {
        errors.push(rulePath + '.value is required for equals.');
      }

      if (rule.operator === 'in' && !Array.isArray(rule.value)) {
        errors.push(rulePath + '.value must be an array for in.');
      }
    });
  }

  function validateOptions(question, path, errors) {
    if (!Array.isArray(question.options)) {
      errors.push(path + '.options must be an array.');
      return;
    }

    const optionValues = [];
    question.options.forEach(function (option, index) {
      const optionPath = path + '.options[' + index + ']';

      if (!isObject(option)) {
        errors.push(optionPath + ' must be an object.');
        return;
      }

      if (!isPrimitive(option.value)) {
        errors.push(optionPath + '.value must be a string, number, or boolean.');
      } else if (containsValue(optionValues, option.value)) {
        errors.push(path + '.options must not contain duplicate values.');
      } else {
        optionValues.push(option.value);
      }

      if (!isNonEmptyString(option.labelKey)) {
        errors.push(optionPath + '.labelKey is required.');
      }
    });

    if ((question.type === 'single-choice' || question.type === 'multi-choice') && !question.options.length) {
      errors.push(path + '.options must not be empty for choice questions.');
    }

    if (question.type !== 'single-choice' && question.type !== 'multi-choice' && question.options.length) {
      errors.push(path + '.options must be empty for non-choice questions.');
    }
  }

  function validateValidationSchema(question, path, errors) {
    const validation = question.validation;

    if (!isObject(validation)) {
      errors.push(path + '.validation must be an object.');
      return;
    }

    if (validation.min !== undefined && !isFiniteNumber(validation.min)) {
      errors.push(path + '.validation.min must be a finite number.');
    }

    if (validation.max !== undefined && !isFiniteNumber(validation.max)) {
      errors.push(path + '.validation.max must be a finite number.');
    }

    if ((validation.min !== undefined || validation.max !== undefined) && question.type !== 'number') {
      errors.push(path + '.validation min and max are supported only for number questions.');
    }

    if (isFiniteNumber(validation.min) && isFiniteNumber(validation.max) && validation.min > validation.max) {
      errors.push(path + '.validation.min must not exceed max.');
    }

    if (validation.allowedValues !== undefined) {
      if (!Array.isArray(validation.allowedValues)) {
        errors.push(path + '.validation.allowedValues must be an array.');
      } else {
        const seenValues = [];
        validation.allowedValues.forEach(function (value) {
          if (!isPrimitive(value)) {
            errors.push(path + '.validation.allowedValues entries must be strings, numbers, or booleans.');
          } else if (containsValue(seenValues, value)) {
            errors.push(path + '.validation.allowedValues must not contain duplicates.');
          } else {
            seenValues.push(value);
          }
        });

        if (question.type === 'single-choice' || question.type === 'multi-choice') {
          const optionValues = question.options.map(function (option) {
            return option && option.value;
          });
          validation.allowedValues.forEach(function (value) {
            if (!containsValue(optionValues, value)) {
              errors.push(path + '.validation.allowedValues must reference configured option values.');
            }
          });
        }
      }
    }

    if (validation.units !== undefined) {
      if (!Array.isArray(validation.units) || !validation.units.length) {
        errors.push(path + '.validation.units must be a non-empty array when configured.');
      } else {
        const seenUnits = new Set();
        validation.units.forEach(function (unit) {
          if (!isNonEmptyString(unit)) {
            errors.push(path + '.validation.units entries must be non-empty strings.');
          } else if (seenUnits.has(unit)) {
            errors.push(path + '.validation.units must not contain duplicates.');
          } else {
            seenUnits.add(unit);
          }
        });
      }
    }
  }

  function validateQuestion(question, index, knownAnswerKeys, questionIds, answerKeys, errors) {
    const path = 'questionnaireEngine.questionRegistry[' + index + ']';

    if (!isObject(question)) {
      errors.push(path + ' must be an object.');
      return;
    }

    validateIdentifier(question.id, path + '.id', errors);
    if (questionIds.has(question.id)) {
      errors.push('questionnaireEngine.questionRegistry must not contain duplicate question ids.');
    }
    questionIds.add(question.id);

    if (!isNonEmptyString(question.version)) {
      errors.push(path + '.version is required.');
    }

    if (!ALLOWED_QUESTION_TYPES.includes(question.type)) {
      errors.push(path + '.type must be one of: ' + ALLOWED_QUESTION_TYPES.join(', ') + '.');
    }

    if (!isNonEmptyString(question.titleKey)) {
      errors.push(path + '.titleKey is required.');
    }
    validateOptionalKey(question.descriptionKey, path + '.descriptionKey', errors);
    validateOptionalKey(question.helpKey, path + '.helpKey', errors);

    if (typeof question.required !== 'boolean') {
      errors.push(path + '.required must be a boolean.');
    }

    validateIdentifier(question.answerKey, path + '.answerKey', errors);
    if (answerKeys.has(question.answerKey)) {
      errors.push('questionnaireEngine.questionRegistry must not contain duplicate answerKey values.');
    }
    answerKeys.add(question.answerKey);

    if (!isNonEmptyString(question.analyticsKey)) {
      errors.push(path + '.analyticsKey is required.');
    }

    validateOptions(question, path, errors);
    validateValidationSchema(question, path, errors);
    validateVisibilityRules(question.visibilityRules, path + '.visibilityRules', knownAnswerKeys, errors);
  }

  function validateSections(sections, questionIds, knownAnswerKeys, errors) {
    if (!Array.isArray(sections)) {
      errors.push('questionnaireEngine.sections must be an array.');
      return new Set();
    }

    const sectionIds = new Set();
    sections.forEach(function (section, index) {
      const path = 'questionnaireEngine.sections[' + index + ']';

      if (!isObject(section)) {
        errors.push(path + ' must be an object.');
        return;
      }

      validateIdentifier(section.id, path + '.id', errors);
      if (sectionIds.has(section.id)) {
        errors.push('questionnaireEngine.sections must not contain duplicate ids.');
      }
      sectionIds.add(section.id);

      if (!isNonEmptyString(section.version)) {
        errors.push(path + '.version is required.');
      }
      validateOptionalKey(section.titleKey, path + '.titleKey', errors);
      validateOptionalKey(section.descriptionKey, path + '.descriptionKey', errors);

      if (!Array.isArray(section.questionIds) || !section.questionIds.length) {
        errors.push(path + '.questionIds must be a non-empty array.');
      } else {
        const references = new Set();
        section.questionIds.forEach(function (questionId) {
          if (!isNonEmptyString(questionId) || !questionIds.has(questionId)) {
            errors.push(path + '.questionIds must reference registered question ids.');
          } else if (references.has(questionId)) {
            errors.push(path + '.questionIds must not contain duplicates.');
          } else {
            references.add(questionId);
          }
        });
      }

      validateVisibilityRules(section.visibilityRules, path + '.visibilityRules', knownAnswerKeys, errors);
    });

    return sectionIds;
  }

  function validateQuestionnaires(questionnaires, sectionIds, knownAnswerKeys, errors) {
    if (!Array.isArray(questionnaires)) {
      errors.push('questionnaireEngine.questionnaires must be an array.');
      return;
    }

    const questionnaireIds = new Set();
    questionnaires.forEach(function (questionnaire, index) {
      const path = 'questionnaireEngine.questionnaires[' + index + ']';

      if (!isObject(questionnaire)) {
        errors.push(path + ' must be an object.');
        return;
      }

      validateIdentifier(questionnaire.id, path + '.id', errors);
      if (questionnaireIds.has(questionnaire.id)) {
        errors.push('questionnaireEngine.questionnaires must not contain duplicate ids.');
      }
      questionnaireIds.add(questionnaire.id);

      if (!isNonEmptyString(questionnaire.version)) {
        errors.push(path + '.version is required.');
      }
      validateOptionalKey(questionnaire.titleKey, path + '.titleKey', errors);
      validateOptionalKey(questionnaire.descriptionKey, path + '.descriptionKey', errors);

      if (!Array.isArray(questionnaire.sectionIds) || !questionnaire.sectionIds.length) {
        errors.push(path + '.sectionIds must be a non-empty array.');
      } else {
        const references = new Set();
        questionnaire.sectionIds.forEach(function (sectionId) {
          if (!isNonEmptyString(sectionId) || !sectionIds.has(sectionId)) {
            errors.push(path + '.sectionIds must reference configured section ids.');
          } else if (references.has(sectionId)) {
            errors.push(path + '.sectionIds must not contain duplicates.');
          } else {
            references.add(sectionId);
          }
        });
      }

      validateVisibilityRules(questionnaire.visibilityRules, path + '.visibilityRules', knownAnswerKeys, errors);
    });
  }

  function evaluateRule(rule, answers) {
    const answer = getAnswerValue(answers, rule.answerKey);

    switch (rule.operator) {
      case 'exists':
        return answer.exists;
      case 'equals':
        return answer.exists && valuesEqual(answer.value, rule.value);
      case 'in':
        return answer.exists && Array.isArray(rule.value) && containsValue(rule.value, answer.value);
      case 'isTrue':
        return answer.exists && answer.value === true;
      default:
        return false;
    }
  }

  function validateAnswerType(question, value, errors) {
    if (isMissingRequiredValue(value)) {
      return;
    }

    if (question.type === 'number' && !isFiniteNumber(value)) {
      errors.push({ code: 'invalid-type', parameters: { expectedType: 'number' } });
    } else if (question.type === 'text' && typeof value !== 'string') {
      errors.push({ code: 'invalid-type', parameters: { expectedType: 'text' } });
    } else if (question.type === 'boolean' && typeof value !== 'boolean') {
      errors.push({ code: 'invalid-type', parameters: { expectedType: 'boolean' } });
    } else if (question.type === 'single-choice' && !isPrimitive(value)) {
      errors.push({ code: 'invalid-type', parameters: { expectedType: 'single-choice' } });
    } else if (question.type === 'multi-choice' && !Array.isArray(value)) {
      errors.push({ code: 'invalid-type', parameters: { expectedType: 'multi-choice' } });
    }
  }

  function validateAllowedValues(value, allowedValues, errors) {
    if (!Array.isArray(allowedValues) || isMissingRequiredValue(value)) {
      return;
    }

    const submittedValues = Array.isArray(value) ? value : [value];
    submittedValues.forEach(function (submittedValue) {
      if (!containsValue(allowedValues, submittedValue)) {
        errors.push({
          code: 'value-not-allowed',
          parameters: { value: cloneValue(submittedValue) }
        });
      }
    });
  }

  /**
   * Creates a valid pending-approval Questionnaire Engine configuration scaffold.
   *
   * @returns {Object} Default Questionnaire Engine configuration.
   */
  function createDefaultQuestionnaireEngineConfig() {
    return {
      schemaVersion: SCHEMA_VERSION,
      status: 'pending-approval',
      questionRegistry: [],
      sections: [],
      questionnaires: [],
      metadata: {
        owner: 'Product Team',
        status: 'Pending Approval',
        lastUpdated: null
      }
    };
  }

  /**
   * Validates the Questionnaire Engine section of a full or standalone configuration.
   *
   * @param {Object} config Full Nutrition Advisor or standalone Questionnaire Engine configuration.
   * @returns {{isValid: boolean, errors: string[]}} Validation result.
   */
  function validateQuestionnaireEngineConfig(config) {
    const errors = [];
    const engine = getQuestionnaireEngine(config);

    if (!engine) {
      return { isValid: false, errors: ['questionnaireEngine configuration is missing.'] };
    }

    if (engine.schemaVersion !== SCHEMA_VERSION) {
      errors.push('questionnaireEngine.schemaVersion must be ' + SCHEMA_VERSION + '.');
    }

    if (!isNonEmptyString(engine.status)) {
      errors.push('questionnaireEngine.status is required.');
    }

    if (!Array.isArray(engine.questionRegistry)) {
      errors.push('questionnaireEngine.questionRegistry must be an array.');
      return { isValid: false, errors: errors };
    }

    const knownAnswerKeys = new Set(engine.questionRegistry.filter(isObject).map(function (question) {
      return question.answerKey;
    }).filter(isNonEmptyString));
    const questionIds = new Set();
    const answerKeys = new Set();

    engine.questionRegistry.forEach(function (question, index) {
      validateQuestion(question, index, knownAnswerKeys, questionIds, answerKeys, errors);
    });

    const sectionIds = validateSections(engine.sections, questionIds, knownAnswerKeys, errors);
    validateQuestionnaires(engine.questionnaires, sectionIds, knownAnswerKeys, errors);

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Returns a cloned registered question by permanent question id.
   *
   * @param {string} questionId Permanent Question Registry identifier.
   * @param {Object} config Full Nutrition Advisor or standalone Questionnaire Engine configuration.
   * @returns {Object|null} Registered question or null when unavailable.
   */
  function getQuestion(questionId, config) {
    const engine = getQuestionnaireEngine(config);

    if (!engine || !Array.isArray(engine.questionRegistry)) {
      return null;
    }

    const question = engine.questionRegistry.find(function (candidate) {
      return candidate && candidate.id === questionId;
    });

    return question ? cloneValue(question) : null;
  }

  /**
   * Evaluates visibility rules using only the supported deterministic operators.
   *
   * @param {Object[]} rules Visibility rules evaluated with logical AND.
   * @param {Object} answers Raw answers keyed by answerKey.
   * @returns {boolean} True when every rule matches.
   */
  function evaluateVisibilityRules(rules, answers) {
    if (!Array.isArray(rules)) {
      return false;
    }

    return rules.every(function (rule) {
      return isObject(rule) && ALLOWED_VISIBILITY_OPERATORS.includes(rule.operator) && evaluateRule(rule, answers || {});
    });
  }

  /**
   * Resolves a questionnaire definition into cloned reusable sections and registry questions.
   *
   * @param {string} questionnaireId Questionnaire definition identifier.
   * @param {Object} config Full Nutrition Advisor or standalone Questionnaire Engine configuration.
   * @returns {Object|null} Resolved questionnaire definition or null when invalid or unavailable.
   */
  function resolveQuestionnaireDefinition(questionnaireId, config) {
    const validation = validateQuestionnaireEngineConfig(config);
    const engine = getQuestionnaireEngine(config);

    if (!validation.isValid || !engine) {
      return null;
    }

    const questionnaire = engine.questionnaires.find(function (candidate) {
      return candidate && candidate.id === questionnaireId;
    });

    if (!questionnaire) {
      return null;
    }

    const sections = questionnaire.sectionIds.map(function (sectionId) {
      const section = engine.sections.find(function (candidate) {
        return candidate && candidate.id === sectionId;
      });
      const resolvedSection = cloneValue(section);
      resolvedSection.questions = section.questionIds.map(function (questionId) {
        return cloneValue(engine.questionRegistry.find(function (candidate) {
          return candidate && candidate.id === questionId;
        }));
      });
      return resolvedSection;
    });

    const resolved = cloneValue(questionnaire);
    resolved.sections = sections;
    return resolved;
  }

  /**
   * Validates one answer against a registered question without coercion or calculation.
   *
   * @param {Object} question Registered question definition.
   * @param {*} rawAnswer Scalar answer or a `{value, unit}` answer object.
   * @returns {{isValid: boolean, errors: Object[]}} Structured validation result.
   */
  function validateAnswer(question, rawAnswer) {
    const errors = [];

    if (!isObject(question)) {
      return {
        isValid: false,
        errors: [{ code: 'invalid-question-definition', parameters: {} }]
      };
    }

    const normalized = normalizeRawAnswer(rawAnswer);
    const value = normalized.value;
    const validation = isObject(question.validation) ? question.validation : {};

    if (question.required && isMissingRequiredValue(value)) {
      errors.push({ code: 'required', parameters: {} });
    }

    validateAnswerType(question, value, errors);

    if (isFiniteNumber(value) && isFiniteNumber(validation.min) && value < validation.min) {
      errors.push({ code: 'below-minimum', parameters: { min: validation.min } });
    }

    if (isFiniteNumber(value) && isFiniteNumber(validation.max) && value > validation.max) {
      errors.push({ code: 'above-maximum', parameters: { max: validation.max } });
    }

    validateAllowedValues(value, validation.allowedValues, errors);

    if (!isMissingRequiredValue(value) && Array.isArray(validation.units)) {
      if (!isNonEmptyString(normalized.unit)) {
        errors.push({ code: 'unit-required', parameters: { allowedUnits: cloneValue(validation.units) } });
      } else if (!validation.units.includes(normalized.unit)) {
        errors.push({
          code: 'unit-not-allowed',
          parameters: {
            unit: normalized.unit,
            allowedUnits: cloneValue(validation.units)
          }
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Builds a deterministic normalized answer envelope for one questionnaire definition.
   *
   * @param {string} questionnaireId Questionnaire definition identifier.
   * @param {Object} rawAnswers Raw answers keyed by registered answerKey.
   * @param {Object} config Full Nutrition Advisor or standalone Questionnaire Engine configuration.
   * @returns {Object} Normalized answer envelope with structured validation errors.
   */
  function normalizeAnswers(questionnaireId, rawAnswers, config) {
    const configValidation = validateQuestionnaireEngineConfig(config);
    const resolved = resolveQuestionnaireDefinition(questionnaireId, config);

    if (!configValidation.isValid) {
      return {
        status: 'invalid-configuration',
        schemaVersion: SCHEMA_VERSION,
        questionnaireId: questionnaireId || null,
        questionnaireVersion: null,
        answers: [],
        validationErrors: [{
          questionId: null,
          answerKey: null,
          errors: [{ code: 'invalid-questionnaire-engine-configuration', parameters: {} }]
        }]
      };
    }

    if (!resolved) {
      return {
        status: 'questionnaire-not-found',
        schemaVersion: SCHEMA_VERSION,
        questionnaireId: questionnaireId || null,
        questionnaireVersion: null,
        answers: [],
        validationErrors: [{
          questionId: null,
          answerKey: null,
          errors: [{ code: 'questionnaire-not-found', parameters: { questionnaireId: questionnaireId || null } }]
        }]
      };
    }

    const safeRawAnswers = isObject(rawAnswers) ? rawAnswers : {};
    const normalizedAnswers = [];
    const validationErrors = [];

    if (evaluateVisibilityRules(resolved.visibilityRules, safeRawAnswers)) {
      resolved.sections.forEach(function (section) {
        if (!evaluateVisibilityRules(section.visibilityRules, safeRawAnswers)) {
          return;
        }

        section.questions.forEach(function (question) {
          if (!evaluateVisibilityRules(question.visibilityRules, safeRawAnswers)) {
            return;
          }

          const hasRawAnswer = Object.prototype.hasOwnProperty.call(safeRawAnswers, question.answerKey);
          const normalized = normalizeRawAnswer(hasRawAnswer ? safeRawAnswers[question.answerKey] : undefined);
          const answerValidation = validateAnswer(question, normalized);

          normalizedAnswers.push({
            questionId: question.id,
            questionVersion: question.version,
            answerKey: question.answerKey,
            analyticsKey: question.analyticsKey,
            value: normalized.value === undefined ? null : normalized.value,
            unit: normalized.unit
          });

          if (!answerValidation.isValid) {
            validationErrors.push({
              questionId: question.id,
              answerKey: question.answerKey,
              errors: answerValidation.errors
            });
          }
        });
      });
    }

    return {
      status: validationErrors.length ? 'invalid' : 'valid',
      schemaVersion: SCHEMA_VERSION,
      questionnaireId: resolved.id,
      questionnaireVersion: resolved.version,
      answers: normalizedAnswers,
      validationErrors: validationErrors
    };
  }

  const api = {
    createDefaultQuestionnaireEngineConfig,
    validateQuestionnaireEngineConfig,
    getQuestion,
    evaluateVisibilityRules,
    resolveQuestionnaireDefinition,
    validateAnswer,
    normalizeAnswers
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.QuestionnaireEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
